<script setup lang="ts" generic="Item extends ItemBase, T extends Constructable">
import { useElementSize } from "@vueuse/core";
import { computed, nextTick, onMounted, onUnmounted, type Reactive, reactive, ref, watch } from "vue";
import { DynamicScroller } from "vue-virtual-scroller";

import DynamicScrollerWithScrollbarOverlay from "~/components/dumb/DynamicScrollerWithScrollbarOverlay.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import { colorsByTheme } from "~/constants/style";
import type { Constructable } from "~/shared/typeUtils";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { getAncestorDuids } from "~/utils/common";
import { makeListComparatorOrganizedBySize, numberComparator } from "~/utils/comparator";
import { getOrdersBetween } from "~/utils/orderManager";

import DragItem from "./DragItem.vue";
import { removeDragGhost } from "./dragGhost";
import useDragStore from "./DragStore";
import {
  type DragPointer,
  registerDragTarget,
  startDragScroll,
  stopDragScroll,
  unregisterDragTarget,
} from "./dragScroll";
import { type ItemBase } from "./shared";

const DEFAULT_CATEGORY = "default";
const PX_PER_INDENT_LEVEL = 30;
const DRAG_ITEM_INDEX_ATTR = "data-index";

const props = withDefaults(
  defineProps<{
    items: Item[];
    component: T;
    getComponentProps: (item: Item, index: number) => InstanceType<T>["$props"];
    minItemSize: number;
    group: string; // Areas with same group can exchange items
    category?: string;
    disabled?: boolean;
    enableOnMobile?: boolean;
    disableParentChange?: boolean;
    placeholderColor?: string;
    dropAreaClasses?: string;
    gap?: number;
    subitemGap?: number;
    itemSidePadding?: number;
    indentationPx?: number;
    indentationLine?: boolean;
    isLoading?: boolean;
    overlayScrollbar?: boolean;
    scrollContainer?: HTMLElement | null;
    dragHandle?: string; // If set, only elements with this class can initiate drag
    miniDropzone?: boolean;
  }>(),
  {
    category: DEFAULT_CATEGORY,
    disabled: false,
    enableOnMobile: false,
    disableParentChange: false,
    placeholderColor: undefined,
    dropAreaClasses: undefined,
    gap: 10,
    subitemGap: 5,
    itemSidePadding: 0,
    indentationPx: 20,
    indentationLine: false,
    isLoading: false,
    overlayScrollbar: false,
    scrollContainer: null,
    dragHandle: undefined,
    miniDropzone: false,
  }
);

const emit = defineEmits<{
  change: [category: string, items: Item[]];
  click: [event: MouseEvent];
  keydown: [event: KeyboardEvent];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const dragStore = useDragStore();
const pageStore = usePageStore();

// Define a compatible type for wrapper that works with both components
type WrapperType = {
  $el?: HTMLElement;
  getItemOffset: (index: number) => number;
  setScrollTop?: (top: number) => void;
  scrollToItem?: (index: number) => void;
} | null;

const wrapper = ref<WrapperType>(null);

const showPlaceholders = computed(() => dragStore.showPlaceholdersForCategory(props.group, props.category));
const isDraggingGroup = computed(() => dragStore.getDraggingItemsForGroup<Item>(props.group).length > 0);
const innerItems = computed(() =>
  showPlaceholders.value ? props.items : dragStore.getListItems<Item>(props.group, props.category)
);

const disabledNorm = computed(
  () => props.disabled || (pageStore.isMobile && !props.enableOnMobile) || pageStore.isPublicView
);
const placeholderColor = computed(() => props.placeholderColor ?? colorsByTheme[pageStore.theme].bgLt);
const fullOrderMap = computed(() => {
  const items = [...innerItems.value];
  const existingOrderMap = new Map(items.map((e, i) => [e.duid, i]));
  const ancestorMap = new Map(items.map((e) => [e.duid, getAncestorDuids(items, e)]));
  return new Map(
    items.map((task) => {
      const orders = (ancestorMap.get(task.duid) ?? []).map((f) => existingOrderMap.get(f) ?? 0);
      orders.push(existingOrderMap.get(task.duid) ?? 0);
      return [task.duid, orders];
    })
  );
});

const flatItems = computed(() => {
  const items = [...innerItems.value];
  const fullOrderComparator = makeListComparatorOrganizedBySize(numberComparator);
  items.sort((a, b) => {
    const aFullOrder = fullOrderMap.value.get(a.duid) ?? [];
    const bFullOrder = fullOrderMap.value.get(b.duid) ?? [];
    return fullOrderComparator(aFullOrder, bFullOrder);
  });
  return items;
});

watch(
  [() => props.items, () => props.group, () => props.category],
  ([newItems, newGroup, newCategory]) => {
    dragStore.setListItems(newGroup, newCategory ?? DEFAULT_CATEGORY, newItems);
  },
  { immediate: true }
);

const resetInnerList = () => {
  if (dragStore.getListItems(props.group, props.category) !== props.items) {
    dragStore.setListItems(props.group, props.category, props.items);
  }
};

const getItemOrder = (list: Item[], newIndex: number) => {
  const itemAboveDest = newIndex === 0 ? undefined : list[newIndex - 1]?.order;
  const itemBelowDest = newIndex < list.length - 1 ? list[newIndex + 1]?.order : undefined;

  return getOrdersBetween(itemAboveDest, itemBelowDest)[0];
};

const getScrollElement = (): HTMLElement | undefined =>
  !props.overlayScrollbar && props.scrollContainer ? props.scrollContainer : wrapper.value?.$el;

const setScrollTop = (top: number) => {
  if (props.overlayScrollbar) {
    wrapper.value?.setScrollTop?.(top);
    return;
  }

  const el = getScrollElement();
  if (el) {
    el.scrollTop = top;
  }
};

const commitDrag = (draggedItems: ItemBase[]) => {
  const currentItemDuids = new Set(innerItems.value.map((e) => e.duid));
  const items = draggedItems
    .filter((e) => currentItemDuids.has(e.duid))
    .map((e) => {
      const index = innerItems.value.findIndex((innerItem) => innerItem.duid === e.duid);
      return { ...e, order: getItemOrder(innerItems.value, index) } as Item;
    });
  if (items.length === 0) {
    return;
  }

  const el = getScrollElement();
  const scrollTop = el?.scrollTop ?? 0;
  emit("change", props.category, items);
  dragStore.resetGroup(props.group);
  nextTick(() => {
    if (el && el.scrollTop !== scrollTop) {
      setScrollTop(scrollTop);
    }
  });
};

const setDraggedItemsParentDuid = (parentDuid: string | null | undefined) => {
  if (props.disableParentChange) {
    return;
  }
  dragStore.setDraggedItemsParentDuid(parentDuid);
};

const applyPlacement = (item: Item, pointerY: number, targetRect: { top: number; height: number }) => {
  const draggingItems = dragStore.getDraggingItemsForGroup<Item>(props.group);
  if (showPlaceholders.value && draggingItems.some((e) => e.duid === item.duid)) {
    const placeholderIndex = innerItems.value.findIndex((e) => e.duid === item.duid);
    dragStore.moveDraggedItemsToIndex(
      props.group,
      props.category,
      innerItems.value
        .slice(0, placeholderIndex)
        .filter((e) => !draggingItems.some((dragged) => dragged.duid === e.duid)).length
    );
    return;
  }

  const pointerRatio = (pointerY - targetRect.top) / targetRect.height;
  if (dragStore.moveDraggedItemsToItem(props.group, props.category, item.duid, pointerRatio)) {
    setDraggedItemsParentDuid(item.parentDuid);
  }
};

const updatePlacement = (pointer: DragPointer) => {
  const row = document.elementsFromPoint(pointer.x, pointer.y).find((e) => e.matches(`[${DRAG_ITEM_INDEX_ATTR}]`));
  if (!row || !wrapper.value?.$el?.contains(row)) {
    return;
  }

  const item = flatItems.value[Number(row.getAttribute(DRAG_ITEM_INDEX_ATTR))];
  if (item) {
    applyPlacement(item, pointer.y, row.getBoundingClientRect());
  }
};

const onDragEnter = (e: DragEvent, item: Item) => {
  e.preventDefault();
  if (!isDraggingGroup.value) {
    return;
  }

  applyPlacement(item, e.clientY, (e.currentTarget as HTMLElement).getBoundingClientRect());
};

const initialMouseX = ref<{ duid: string; x: number } | null>(null);
const onDragOver = (e: DragEvent, item: Item) => {
  e.preventDefault();
  if (!isDraggingGroup.value) {
    return;
  }

  if (initialMouseX.value === null || initialMouseX.value.duid !== item.duid) {
    initialMouseX.value = { duid: item.duid, x: e.clientX };
  }

  const deltaX = e.clientX - initialMouseX.value.x;
  if (Math.abs(deltaX) < PX_PER_INDENT_LEVEL) {
    return;
  }

  if (deltaX < 0) {
    const parentItem = innerItems.value.find((innerItem) => innerItem.duid === item.parentDuid);
    setDraggedItemsParentDuid(parentItem ? parentItem.parentDuid : item.parentDuid);
  } else {
    const index = innerItems.value.findIndex((innerItem) => innerItem.duid === item.duid);
    const itemAbove = index > 0 ? innerItems.value[index - 1] : undefined;
    let newParentDuid = item.parentDuid;

    if (itemAbove) {
      const ancestorDuids = getAncestorDuids(innerItems.value, itemAbove);
      const currentAncestorLevel = getAncestorDuids(innerItems.value, item).length;
      if (currentAncestorLevel < ancestorDuids.length) {
        newParentDuid = ancestorDuids[currentAncestorLevel];
      } else if (currentAncestorLevel === ancestorDuids.length) {
        newParentDuid = itemAbove.duid;
      }
    }
    setDraggedItemsParentDuid(newParentDuid);
  }
  initialMouseX.value.x = e.clientX;
};

const moveDraggedItemsToEnd = () => {
  if (!isDraggingGroup.value) {
    return;
  }

  setDraggedItemsParentDuid(null);
  dragStore.moveDraggedItemsToIndex(
    props.group,
    props.category,
    dragStore.getListItems(props.group, props.category).length
  );
};

const onDragOverDropzone = (e: DragEvent) => {
  e.preventDefault();
  moveDraggedItemsToEnd();
};

const onDragEnd = () => {
  document.body.classList.remove("dragging-item");
  if (!isDraggingGroup.value) {
    return;
  }

  stopDragScroll();
  removeDragGhost();
  dragStore.finishDrag(props.group);
  appStore.dragging = null;
  initialMouseX.value = null;
};

const startDrag = (item: Item) => {
  const draggedItems =
    appStore.selectedTaskDuids.has(item.duid) && appStore.selectedTaskDuids.size > 1
      ? dataStore
          .getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
            includeTrashed: false,
            includeDraft: false,
          })
          .map((task) => task)
      : [item];

  dragStore.setDraggingItems(props.group, props.category, draggedItems);
  appStore.dragging = { group: props.group, category: props.category };
  startDragScroll(props.group, onDragEnd);
};

const { height: scrollerHeight } = useElementSize(() => wrapper.value?.$el ?? null);
const itemsHeight = computed(() => wrapper.value?.getItemOffset(flatItems.value.length) ?? 0);

const dropzoneHeight = computed(() => {
  if (!wrapper.value) {
    return 0;
  }
  if (props.miniDropzone) {
    return innerItems.value.length > 0 ? 0 : 1;
  }
  return Math.max(props.minItemSize, scrollerHeight.value - itemsHeight.value);
});

const dragTarget = { getScrollElement, setScrollTop, updatePlacement };

onMounted(() => {
  dragStore.registerResetListener(props.group, resetInnerList);
  dragStore.registerCommitListener(props.group, props.category, commitDrag);
  registerDragTarget(props.group, dragTarget);
});

onUnmounted(() => {
  dragStore.unregisterResetListener(props.group, resetInnerList);
  dragStore.unregisterCommitListener(props.group, props.category, commitDrag);
  unregisterDragTarget(props.group, dragTarget);
});

const itemRefs = reactive(new Map()) satisfies Reactive<Map<string, InstanceType<T>>>;
const assignItemRef = (itemDuid: string, e: { wrapper: HTMLDivElement; itemRef: InstanceType<T> } | null) => {
  if (!e) {
    return;
  }
  itemRefs.set(itemDuid, e.itemRef);
};

const scrollToItem = (duid: string) => {
  const index = flatItems.value.findIndex((e) => e.duid === duid);
  if (index === -1 || !wrapper.value) {
    return;
  }
  wrapper.value.scrollToItem?.(index);
};

defineExpose({
  itemRefs,
  moveDraggedItemsToEnd,
  scrollToItem,
});
</script>

<template>
  <component
    :is="overlayScrollbar ? DynamicScrollerWithScrollbarOverlay : DynamicScroller"
    ref="wrapper"
    :items="flatItems"
    :min-item-size="minItemSize + Math.min(subitemGap, gap)"
    key-field="duid"
    :scroll-parent="scrollContainer"
    :page-mode="overlayScrollbar ? undefined : Boolean(scrollContainer)"
    class="relative h-full min-h-px flex-1"
    :class="[
      !overlayScrollbar && 'overflow-x-hidden overflow-y-auto',
      isDraggingGroup && (dropAreaClasses ? dropAreaClasses : 'dart-drag-drop-area'),
    ]"
    @click="emit('click', $event)"
    @keydown="emit('keydown', $event)"
    @dragover.prevent
    @drop.prevent>
    <template #before>
      <slot v-if="innerItems.length && !isDraggingGroup" name="before" />
      <!-- No items state -->
      <div
        v-if="!innerItems.length && !isDraggingGroup"
        :id="`${group}-${category}-noitems`"
        class="hidden select-none only:block">
        <slot />
      </div>
    </template>
    <template #default="{ item, active, index }">
      <DragItem
        :ref="(elem) => assignItemRef(item.duid, elem as never)"
        :key="item.duid"
        :index="index ?? 0"
        :active-in-scroll="active"
        :level="fullOrderMap.get(item.duid)?.length ?? 1"
        :current-item="item"
        :all-items="items"
        :group="group"
        :category="category"
        :component="props.component"
        :get-component-props="props.getComponentProps"
        :placeholder-color="placeholderColor"
        :disabled="disabledNorm || Boolean(item.disabled)"
        :gap="props.gap"
        :subitem-gap="props.subitemGap"
        :item-side-padding="props.itemSidePadding"
        :indentation-px="props.indentationPx"
        :indentation-line="props.indentationLine"
        :drag-handle="props.dragHandle"
        @drag-start="() => startDrag(item)"
        @drag-end="onDragEnd"
        @drag-over="(e) => onDragOver(e, item)"
        @drag-enter="(e) => onDragEnter(e, item)" />
    </template>
    <template #after>
      <LoadingSpinner v-if="isLoading" class="my-2 overflow-hidden" small />
      <div
        v-if="isDraggingGroup"
        id="dropzone"
        class="size-full"
        :style="{ minHeight: `${dropzoneHeight}px` }"
        @dragenter="onDragOverDropzone" />
      <slot v-if="innerItems.length && !isDraggingGroup" name="after" />
    </template>
  </component>
</template>

<style scoped>
@reference "#frontend.css";
/* Drop area styles */
.dart-drag-drop-area {
  @apply rounded !bg-app-light-lt/50 dark:!bg-app-dark-lt/50;
}
</style>
